import mergeWith from 'lodash/mergeWith';
import { ulid } from 'ulid';

import type {
  BaseDocument,
  FirstClassDocument,
  Highlight,
  Note,
  ParentDocument,
  PartialDocument,
  TransientDocumentData,
  UserEvent,
} from '../../../../types';
import { Category } from '../../../../types';
import type { DocumentTag, DocumentTagsObject } from '../../../../types/tags';
import { notEmpty } from '../../../../typeValidators';
import nowTimestamp from '../../../../utils/dates/nowTimestamp';
import { appCategory, isDevOrTest, isExtension } from '../../../../utils/environment';
// eslint-disable-next-line import/no-cycle
import exceptionHandler from '../../../../utils/exceptionHandler.platform';
import makeLogger from '../../../../utils/makeLogger';
import checkIfWeShouldSetFocusedHighlightIdWhenCreatingAHighlight from '../../../checkIfWeShouldSetFocusedHighlightIdWhenCreatingAHighlight';
import foreground from '../../../portalGates/contentFrame/to/reactNativeWebview';
import { getDocument, getDocuments, getNoteFromHighlight } from '../../../stateGetters';
import { createToast } from '../../../toasts.platform';
import type {
  StateUpdateOptions,
  StateUpdateOptionsWithoutEventName,
  StateUpdateResult,
} from '../../../types';
import { trackUserEvent } from '../../../userEvents';
import { resolveChapterBreadcrumb } from '../../../utils/resolveChapterBreadcrumb';
import { applyDRMTruncationIfNeeded } from '../../../utils/truncateForDRMStorage';
import { deleteReaderFile } from '../../../utils/uploadFiles';
import { setFocusedHighlightId } from '../../transientStateUpdaters/other';
import { upsertDocument } from './create';
import { deleteDocumentById, deleteDocumentsById } from './remove';
import { updateDocument } from './update';

const logger = makeLogger(__filename);

const source = isExtension ? 'Readwise web highlighter' : `reader-${appCategory}`;

export const createHighlightDocumentObject = (
  highlightData: Partial<Highlight> &
    Pick<Highlight, 'content' | 'html' | 'location' | 'markdown' | 'offset' | 'source_specific_data'> & {
      parent: ParentDocument['id'];
    },
): Highlight & { parent: ParentDocument['id']; } => {
  if (isDevOrTest) {
    const pieces = highlightData.location.split(',').map((piece) => piece.split(':'));
    if (pieces[0][0] === pieces[1][0] && parseInt(pieces[0][1], 10) > parseInt(pieces[1][1], 10)) {
      logger.warn('highlight location is in incorrect order', highlightData);
    }
  }

  if (!highlightData.content && !highlightData.html && !highlightData.markdown) {
    exceptionHandler.captureException(new Error(`Highlight content is empty`), {
      extra: {
        highlightData,
      },
    });
  }

  const savedAt = nowTimestamp();
  return {
    ...highlightData,
    category: highlightData.category ?? Category.Highlight,
    // This should be populated separately
    children: highlightData.children ?? [],
    id: highlightData.id ?? ulid().toLowerCase(),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    saved_at: highlightData.saved_at ?? savedAt,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    saved_at_history: highlightData.saved_at_history ?? [savedAt],
    source,
    source_specific_data: highlightData.source_specific_data ?? {},
    tags: highlightData.tags ?? {},
  };
};

export const createNoteDocument = ({
  content,
  highlightId,
  savedAt,
}: { content: Highlight['content']; highlightId: Highlight['id']; savedAt: number; }): Note => {
  return {
    category: Category.Note,
    content,
    id: ulid().toLowerCase(),
    parent: highlightId,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    saved_at: savedAt,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    saved_at_history: [savedAt],
    source,
  };
};

function getChapterContext(
  parentDoc: FirstClassDocument | null,
  highlightOffset: number | undefined,
) {
  if (highlightOffset == null || parentDoc?.category !== Category.EPUB) {
    return null;
  }
  const tocItems = parentDoc.source_specific_data?.epub?.toc;
  if (!tocItems || !tocItems.some((item) => item.offset != null)) {
    return null;
  }
  const breadcrumb = resolveChapterBreadcrumb(highlightOffset, tocItems);
  return breadcrumb.length > 0 ? breadcrumb : null;
}

export const createHighlight = async (
  highlightDetails: Parameters<typeof createHighlightDocumentObject>[0],
  transientDocumentData: Partial<TransientDocumentData>,
  {
    correlationId,
    shouldFocusOnCreation = true,
    userInteraction = 'unknown',
  }: {
    correlationId?: UserEvent['correlationId'];
    shouldFocusOnCreation?: boolean;
    userInteraction?: NonNullable<UserEvent['userInteraction']>['name'];
  },
): Promise<{
  highlightId: Highlight['id'];
  stateUpdateResult: Awaited<StateUpdateResult>;
}> => {
  if (!highlightDetails.content && !highlightDetails.html && !highlightDetails.markdown) {
    exceptionHandler.captureException(new Error(`Highlight content is empty`), {
      extra: {
        highlightDetails,
      },
    });
  }

  // DRM compliance: truncate highlights to 250 words for protected books (PRH, HarperCollins).
  // The location string still references the full selection; truncation only affects stored content.
  const parentDoc = await getDocument<FirstClassDocument>(highlightDetails.parent);
  const truncationResult = applyDRMTruncationIfNeeded(
    {
      text: highlightDetails.content ?? '',
      html: highlightDetails.html ?? '',
      markdown: highlightDetails.markdown ?? '',
    },
    parentDoc,
  );

  const chapterContext = getChapterContext(parentDoc, highlightDetails.offset);
  if (chapterContext) {
    highlightDetails.source_specific_data = {
      ...highlightDetails.source_specific_data,
      chapter_context: chapterContext,
    };
  }

  const doc = createHighlightDocumentObject({
    ...highlightDetails,
    content: truncationResult.content,
    html: truncationResult.html,
    markdown: truncationResult.markdown,
    source_specific_data: {
      ...highlightDetails.source_specific_data,
      drm_truncated: truncationResult.wasTruncated || undefined,
    },
  });

  const stateUpdateResult = await upsertDocument(doc, transientDocumentData, {
    correlationId,
    eventName: 'highlight-created',
    userInteraction,
  });

  if (shouldFocusOnCreation && await checkIfWeShouldSetFocusedHighlightIdWhenCreatingAHighlight(userInteraction)) {
    await setFocusedHighlightId(doc.id);
  }

  return {
    highlightId: doc.id,
    stateUpdateResult,
  };
};

export const createHighlightNote = async (
  { content, highlightId, savedAt }: Parameters<typeof createNoteDocument>[0],
  options: StateUpdateOptionsWithoutEventName,
): StateUpdateResult => {
  if (!content) {
    throw new Error('No content');
  }

  const note = createNoteDocument({ content, highlightId, savedAt });
  return upsertDocument(
    note,
    {},
    {
      ...options,
      eventName: 'note-created',
    },
  );
};

export const appendToHighlightNote = async (
  highlightId: Highlight['id'],
  contentToAdd: string,
  options: StateUpdateOptionsWithoutEventName,
): StateUpdateResult => {
  const highlight = await getDocument<Highlight>(highlightId);
  if (!highlight) {
    throw new Error('Highlight not found');
  }

  const existingNote = await getNoteFromHighlight(highlight);

  const existingNoteTrimmed = existingNote.trim();
  const contentToAddTrimmed = contentToAdd.trim();

  if (!existingNote) {
    return createHighlightNote({
      content: contentToAddTrimmed,
      highlightId,
      savedAt: nowTimestamp(),
    }, options);
  }

  return updateHighlightNote(
    highlight,
    [existingNoteTrimmed, contentToAddTrimmed].join('\n\n---\n\n'),
    options,
  );
};

export const updateHighlight = async (
  highlightId: Highlight['id'],
  updates: Partial<Highlight>,
): ReturnType<typeof updateDocument> => {
  let finalUpdates = updates;

  if (updates.content || updates.html || updates.markdown) {
    const existingHighlight = await getDocument<Highlight>(highlightId);
    if (!existingHighlight || !existingHighlight.parent) {
      throw new Error('updateHighlight: highlight or parent not found for DRM truncation check');
    }

    const highlightWithUpdates = {
      ...existingHighlight,
      ...updates,
    };

    const parentDoc = await getDocument<FirstClassDocument>(existingHighlight.parent);
    const truncationResult = applyDRMTruncationIfNeeded(
      {
        text: highlightWithUpdates.content ?? '',
        html: highlightWithUpdates.html ?? '',
        markdown: highlightWithUpdates.markdown ?? '',
      },
      parentDoc,
    );

    finalUpdates = {
      ...updates,
      content: truncationResult.content,
      html: truncationResult.html,
      markdown: truncationResult.markdown,
      source_specific_data: {
        ...highlightWithUpdates.source_specific_data,
        drm_truncated: truncationResult.wasTruncated || undefined,
      },
    };
  }

  return updateDocument<Highlight>(
    highlightId,
    (existingHighlight) => {
      const savedAtHistory = existingHighlight.saved_at_history ?? [];
      savedAtHistory.push(nowTimestamp());

      return {
        ...existingHighlight,
        ...finalUpdates,
        saved_at: nowTimestamp(),
        saved_at_history: savedAtHistory,
      } as Highlight;
    },
    { eventName: 'update-highlight', userInteraction: 'unknown' },
  );
};

export async function mergeHighlights(
  {
    otherHighlightIds,
    remainingHighlightDetails,
    sourceUrl,
  }: {
    otherHighlightIds: Highlight['id'][];
    remainingHighlightDetails: Pick<
      Highlight,
      'content' | 'source_specific_data' | 'html' | 'id' | 'location' | 'offset'
    >;
    sourceUrl: string;
  },
  optionsArg: StateUpdateOptionsWithoutEventName & { userInteraction: string; },
): ReturnType<typeof upsertDocument> {
  const options: StateUpdateOptions = {
    eventName: 'highlights-merged',
    correlationId: ulid(),
    ...optionsArg,
  };
  const markdown = await foreground.convertHtmlToText(remainingHighlightDetails.html, sourceUrl);
  const savedAt = nowTimestamp();

  const [remainingHighlightBeforeAnyUpdates, otherHighlights] = await Promise.all([
    getDocument<Highlight>(remainingHighlightDetails.id),
    getDocuments<Highlight>(otherHighlightIds),
  ]);
  if (!remainingHighlightBeforeAnyUpdates) {
    throw new Error("Can't find remaining document");
  }

  const parentDoc = remainingHighlightBeforeAnyUpdates.parent
    ? await getDocument<FirstClassDocument>(remainingHighlightBeforeAnyUpdates.parent)
    : null;
  const truncationResult = applyDRMTruncationIfNeeded(
    {
      text: remainingHighlightDetails.content ?? '',
      html: remainingHighlightDetails.html ?? '',
      markdown,
    },
    parentDoc,
  );

  // Gather all the notes first
  const noteIds = [remainingHighlightBeforeAnyUpdates, ...otherHighlights ?? []]
    .filter(notEmpty)
    .sort((a, b) => (a.offset ?? -1) - (b.offset ?? -1))
    .map((highlightInvolved) => highlightInvolved.children.map((noteId) => noteId))
    .flat();
  const noteDocuments = await getDocuments<Note>(noteIds);
  const notes = (noteDocuments ?? []).map((note) => note.content).filter(notEmpty);
  // Delete old note on remaining highlight, plus other highlights and their notes
  const allDocumentIdsToDelete = [...remainingHighlightBeforeAnyUpdates.children, ...otherHighlightIds];
  await deleteDocumentsById(allDocumentIdsToDelete, {
    shouldDeleteChildren: true,
    ...options,
  });

  // Combine tags. If the same tag exists more than once, take the one with the highest created
  const documentTagObjects: DocumentTagsObject[] = [
    remainingHighlightBeforeAnyUpdates,
    ...otherHighlights ?? [],
  ].map((highlight) => highlight.tags ?? {});
  const combinedTags: DocumentTagsObject = mergeWith(
    {},
    ...documentTagObjects,
    (documentTagA: DocumentTag, documentTagB: DocumentTag) => {
      if (!documentTagA) {
        return documentTagB;
      }
      if (!documentTagB) {
        return documentTagA;
      }
      return documentTagA.created >= documentTagB.created ? documentTagA : documentTagB;
    },
  );

  // Update remaining highlight
  const savedAtHistory = remainingHighlightBeforeAnyUpdates.saved_at_history ?? [
    remainingHighlightBeforeAnyUpdates.saved_at,
  ];
  savedAtHistory.push(savedAt);
  const mergedHighlight = {
    ...remainingHighlightBeforeAnyUpdates,
    ...remainingHighlightDetails,
    content: truncationResult.content,
    html: truncationResult.html,
    markdown: truncationResult.markdown,
    children: [], // Remove existing note references
    saved_at: savedAt,
    saved_at_history: savedAtHistory,
    tags: combinedTags,
    source_specific_data: {
      ...remainingHighlightDetails.source_specific_data,
      drm_truncated: truncationResult.wasTruncated || undefined,
    },
  } as Highlight;
  const upsertHighlightPromise = await upsertDocument(
    mergedHighlight,
    {},
    {
      ...options,
      disableWarningForDuplicateChild: true,
    },
  );

  // Create new highlight note
  let upsertNotePromise;
  if (notes.length > 0) {
    const mergedNote = createNoteDocument({
      content: notes.join('\n\n---\n\n'),
      highlightId: remainingHighlightDetails.id,
      savedAt,
    });
    upsertNotePromise = upsertDocument(mergedNote, {}, options);
  }
  const [result] = await Promise.all([upsertHighlightPromise, upsertNotePromise]);

  if (otherHighlights.length >= 1) {
    createToast({
      category: 'success',
      content: `${otherHighlights.length + 1} highlights merged`,
      undoableUserEventId: result.userEvent?.id,
    });
  }

  return result;
}

export const updateHighlightNote = async (
  highlight: Highlight | PartialDocument<Highlight, 'children' | 'id'>,
  noteInput: string,
  options: StateUpdateOptionsWithoutEventName,
): StateUpdateResult => {
  const oldNote = await getNoteFromHighlight(highlight);
  const note = noteInput.trim().length ? noteInput : '';
  if (oldNote === note) {
    return {};
  }

  const userEvent = trackUserEvent({ ...options, eventName: 'highlight-note-updated' });

  for (const childId of highlight.children) {
    await deleteHighlightNote(childId, {
      correlationId: userEvent.correlationId,
      isUndoable: false,
      userInteraction: null, // this is really just internal wiring
    });
  }

  if (note) {
    createHighlightNote(
      {
        content: note,
        highlightId: highlight.id.toString(),
        savedAt: nowTimestamp(),
      },
      {
        correlationId: userEvent.correlationId,
        ...options,
        userInteraction: null, // this is really just internal wiring
      },
    );
  }

  return { userEvent };
};

function cleanupReaderFileForHighlight(highlight: Highlight) {
  const readerFileId = highlight?.source_specific_data?.pdf_highlight?.reader_file_id;

  if (readerFileId) {
    deleteReaderFile({ readerFileId });
  }
}

export const deleteHighlight = async (
  documentId: Highlight['id'],
  options: { shouldShowToast?: boolean; } & Omit<
    Parameters<typeof deleteDocumentById>[1],
    'eventName' | 'shouldDeleteChildren'
  >,
): Promise<void> => {
  const highlight = await getDocument<Highlight>(documentId);
  if (!highlight) {
    logger.error('No such highlight to delete', { documentId });
    return;
  }
  cleanupReaderFileForHighlight(highlight);

  const updateResult = await deleteDocumentById(documentId, {
    ...options,
    shouldDeleteChildren: true,
    eventName: 'highlight-deleted',
  });
  if (options.shouldShowToast !== false) {
    createToast({
      content: `Highlight deleted`,
      category: 'success',
      undoableUserEventId: updateResult.userEvent?.id,
    });
  }
};

export async function deleteHighlights(
  documentIds: Highlight['id'][],
  options: Omit<Parameters<typeof deleteDocumentsById>[1], 'eventName' | 'shouldDeleteChildren'>,
) {
  const highlights = await getDocuments<Highlight>(documentIds);
  if (!highlights) {
    logger.error('No such highlights to delete', { documentIds });
    return;
  }
  highlights.forEach(cleanupReaderFileForHighlight);
  await deleteDocumentsById(documentIds, {
    ...options,
    eventName: 'highlights-deleted',
    shouldDeleteChildren: true,
  });
}

export async function deleteHighlightNote(
  documentId: BaseDocument['id'],
  options: Omit<Parameters<typeof deleteDocumentById>[1], 'eventName' | 'shouldDeleteChildren'>,
): StateUpdateResult {
  return deleteDocumentById(documentId, {
    ...options,
    shouldDeleteChildren: false,
    eventName: 'note-deleted',
  });
}
